import type { SoccerPlayerSummaryStats } from '@frontend/core/lib/domains/reporting'
import { getStatValue } from '@frontend/core/lib/domains/reporting'
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@frontend/ui'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { compareBarCategories } from '../lib/constants'
import { PositionBadge } from './position-badge'

type SortDir = 'asc' | 'desc'

interface PlayerRow {
  player: {
    id: string
    number?: number | null
    displayName?: string
    firstName?: string
    lastName?: string
    position?: string | null
    image?: string
  }
  stats: SoccerPlayerSummaryStats | null | undefined
}

interface CompareTableProps {
  playerRows: PlayerRow[]
  teamId: string
  sessionId: string
  sortField: string
  sortDir: SortDir
  toggleSort: (field: string) => void
  // Called when a user clicks a coloured bar segment. Parent decides what
  // to do — in the team-stats view it isolates the metric's statKey as
  // the active sort (descending). Kept separate from the category
  // visibility set so the component stays presentational.
  onIsolateCategory?: (categoryId: string) => void
  // Full list of currently-visible category ids. Checkbox state + bar
  // segment rendering both read from this. Parent-controlled so segment
  // click + header checkbox share one source of truth.
  visibleCategories?: Set<string>
  onToggleCategory?: (categoryId: string) => void
}

export function CompareTable({
  playerRows,
  teamId,
  sessionId,
  sortField,
  sortDir,
  toggleSort,
  onIsolateCategory,
  visibleCategories: visibleCategoriesProp,
  onToggleCategory
}: CompareTableProps) {
  // Uncontrolled fallback — when parent doesn't pass state, default to all
  // categories visible. In the current reporting app the parent wires
  // `onIsolateCategory` but not the visibility props; we derive visibility
  // locally so isolation + checkbox continue to share one source of truth.
  const [localVisible, setLocalVisible] = useState<Set<string>>(
    () => new Set(compareBarCategories.map((c) => c.id))
  )
  const visibleCategories = visibleCategoriesProp ?? localVisible

  const toggleCategory = (id: string) => {
    if (onToggleCategory) {
      onToggleCategory(id)
      return
    }
    setLocalVisible((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Isolate a single category (or un-isolate if already isolated). Pairs
  // with the parent's `onIsolateCategory` which drives the sort field.
  const onSegmentClick = (categoryId: string) => {
    const isIsolated =
      visibleCategories.size === 1 && visibleCategories.has(categoryId)
    if (isIsolated) {
      setLocalVisible(new Set(compareBarCategories.map((c) => c.id)))
      // Leave sort as-is on un-isolate; user can re-sort manually.
      return
    }
    setLocalVisible(new Set([categoryId]))
    onIsolateCategory?.(categoryId)
  }

  const maxTotal = useMemo(() => {
    let max = 0
    for (const row of playerRows) {
      let sum = 0
      for (const cat of compareBarCategories) {
        if (!visibleCategories.has(cat.id)) continue
        const val =
          getStatValue(
            row.stats?.[cat.statKey as keyof SoccerPlayerSummaryStats]
          ) ?? 0
        sum += val
      }
      if (sum > max) max = sum
    }
    return max
  }, [playerRows, visibleCategories])

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null
    return sortDir === 'asc' ? (
      <ChevronUp className='inline h-3 w-3' />
    ) : (
      <ChevronDown className='inline h-3 w-3' />
    )
  }

  const handleSortKeyDown = (field: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleSort(field)
    }
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='min-h-0 flex-1 overflow-x-auto overflow-y-auto'>
        <Table>
          <TableHeader className='bg-card sticky top-0 z-10'>
            <TableRow className='border-border hover:bg-transparent'>
              <TableHead
                className='text-muted-foreground w-0 cursor-pointer'
                tabIndex={0}
                onClick={() => toggleSort('number')}
                onKeyDown={(e) => handleSortKeyDown('number', e)}
              >
                # <SortIcon field='number' />
              </TableHead>
              <TableHead
                className='text-muted-foreground w-0 cursor-pointer whitespace-nowrap'
                tabIndex={0}
                onClick={() => toggleSort('player')}
                onKeyDown={(e) => handleSortKeyDown('player', e)}
              >
                Player <SortIcon field='player' />
              </TableHead>
              <TableHead className='text-muted-foreground w-0 whitespace-nowrap'>
                Pos.
              </TableHead>
              <TableHead className='text-muted-foreground w-full'>
                <div className='flex items-center gap-4'>
                  {compareBarCategories.map((cat) => (
                    <label key={cat.id} className='flex items-center gap-1.5'>
                      <Checkbox
                        name={`compare-category-${cat.id}`}
                        value={visibleCategories.has(cat.id)}
                        onChange={() => toggleCategory(cat.id)}
                        className='border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background h-3.5 w-3.5 border'
                      />
                      <div
                        className='h-3 w-0.5'
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className='text-xs'>{cat.label}</span>
                    </label>
                  ))}
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {playerRows.map(({ player, stats }) => {
              if (!player) return null

              return (
                <TableRow key={player.id} className='border-border'>
                  <TableCell className='text-foreground w-0 font-medium tabular-nums'>
                    {player.number}
                  </TableCell>
                  <TableCell className='text-foreground w-0 font-medium whitespace-nowrap'>
                    <Link
                      to={`/player/${player.id}?team=${teamId}&session=${sessionId}`}
                      className='hover:underline'
                    >
                      {player.displayName ||
                        `${player.firstName} ${player.lastName}`}
                    </Link>
                  </TableCell>
                  <TableCell className='w-0 whitespace-nowrap'>
                    <PositionBadge position={player.position} />
                  </TableCell>
                  <TableCell>
                    <div className='flex h-6 items-center'>
                      {maxTotal > 0 &&
                        compareBarCategories.map((cat) => {
                          if (!visibleCategories.has(cat.id)) return null
                          const val =
                            getStatValue(
                              stats?.[
                                cat.statKey as keyof SoccerPlayerSummaryStats
                              ]
                            ) ?? 0
                          if (val === 0) return null
                          const widthPct = (val / maxTotal) * 100
                          const isIsolated =
                            visibleCategories.size === 1 &&
                            visibleCategories.has(cat.id)
                          return (
                            <Tooltip key={cat.id}>
                              <TooltipTrigger asChild>
                                <Button
                                  type='button'
                                  variant='ghost'
                                  aria-label={
                                    isIsolated
                                      ? `Show all categories`
                                      : `Isolate ${cat.label}`
                                  }
                                  onClick={() => onSegmentClick(cat.id)}
                                  className='h-full rounded-none p-0 text-xs font-medium text-white transition-opacity hover:bg-transparent hover:opacity-80'
                                  style={{
                                    width: `${widthPct}%`,
                                    backgroundColor: cat.color,
                                    minWidth: val > 0 ? '24px' : 0
                                  }}
                                >
                                  {val}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent variant='popover'>
                                {cat.label}: {val}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
